import { ColDef, ColumnState, GridApi } from 'ag-grid-community';
import { useMemo } from 'react';
import { withOverrides } from '../../../../util/ag-grid-utils';
import { useSchemaToGrid } from '../../../../util/schema-utils';
import {
  CaptableSummaryRowData2FieldsToShow,
  captableSummaryRowData2GridSchema,
  ICaptableSummaryRowData2,
} from './CaptableSummaryRowData2';

export const CaptableGroupingMode = {
  stakeholder: 'stakeholder',
  shareClass: 'share-class',
} as const;

export type CaptableGroupingMode = (typeof CaptableGroupingMode)[keyof typeof CaptableGroupingMode];

// Column state per grouping mode - defines rowGroup and rowGroupIndex
// Note: All 4 columns must be specified to ensure proper state reset when switching modes
export const GroupingColumnState: Record<CaptableGroupingMode, ColumnState[]> = {
  [CaptableGroupingMode.stakeholder]: [
    { colId: 'stakeholderName', rowGroup: true, rowGroupIndex: 0, hide: true },
    { colId: 'fundName', rowGroup: true, rowGroupIndex: 1, hide: true },
    { colId: 'investmentType', rowGroup: true, rowGroupIndex: 2, hide: true },
    { colId: 'investmentName', rowGroup: false, hide: false },
  ],
  [CaptableGroupingMode.shareClass]: [
    { colId: 'investmentType', rowGroup: true, rowGroupIndex: 0, hide: true },
    { colId: 'investmentName', rowGroup: true, rowGroupIndex: 1, hide: true },
    { colId: 'stakeholderName', rowGroup: true, rowGroupIndex: 2, hide: true },
    { colId: 'fundName', rowGroup: false, hide: false },
  ],
};

export function applyGroupingState(api: GridApi, mode: CaptableGroupingMode) {
  api.applyColumnState({
    state: GroupingColumnState[mode],
    applyOrder: true,
  });
}

export function useCaptableSummaryColDefs2(): ColDef<ICaptableSummaryRowData2>[] {
  const schemaToColDefs = useSchemaToGrid();

  return useMemo(() => {
    const defs = schemaToColDefs(captableSummaryRowData2GridSchema(), [
      ...CaptableSummaryRowData2FieldsToShow,
    ]) as ColDef<ICaptableSummaryRowData2>[];

    // Only override settings that cannot be expressed in the schema
    return withOverrides(defs, {
      stakeholderName: {
        enableRowGroup: true,
        getQuickFilterText: (params) => params.value ?? '',
      },
      fundName: {
        enableRowGroup: true,
      },
      investmentType: {
        enableRowGroup: true,
      },
      investmentName: {
        enableRowGroup: true,
      },
      amount: {
        enableValue: true,
        headerClass: 'ag-left-aligned-header',
      },
      sharesHeld: {
        enableValue: true,
      },
      outstandingShares: {
        enableValue: true,
      },
      ownershipPercentage: {
        enableValue: true,
      },
      outstandingOwnershipPercentage: {
        enableValue: true,
      },
    });
  }, [schemaToColDefs]);
}

export const CaptableSummaryDefaultColDefs2: ColDef<ICaptableSummaryRowData2> = {
  flex: 1,
  minWidth: 150,
  sortable: true,
  resizable: true,
  getQuickFilterText: () => '',
};
